import { Download } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";

import Search from "../../../components/Search";
import {
  create,
  deleteCustomChannel,
  exportAll,
  update,
  useList,
  isInUse,
} from "../../../services/customChannel";
import { getLocal, getToken } from "../../../storage/auth";
import { CustomChannelProps } from "../../../types/customChannel";
import DataTable from "./components/DataTable";
import CustomChannelForm from "./components/customChannelForm";
import { hasClaim } from "../../../storage/userClaims";
import { useSnackbar } from "notistack";

interface IParams {
  page: number;
  keyword: string;
  sort: string;
  limit: number;
  uri: string;
  param: string;
}

export default function CustomChann() {
  const { enqueueSnackbar } = useSnackbar();
  const [customChannel, setcustomChannel] = useState<CustomChannelProps>({
    ATIVO: true,
  } as CustomChannelProps);
  const [customChannelId, setcustomChannelId] = useState<string | number>();
  const [isSaving, setIsSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [token, setToken] = useState<string>();
  const [userValidation, setUserValidation] = useState<string>();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [controlEdit, setControlEdit] = useState({
    loading: false,
    isInUse: false,
  });
  const [readOnly, setReadOnly] = useState<boolean>(false);

  const getTokenSession = () => {
    const getTokens = (getToken()) as any;
    const getUser = (getLocal("userInfo")) as any;
    setToken(getTokens);
    setUserValidation(JSON.parse(getUser));
  };

  useEffect(() => {
    getTokenSession();
  }, []);

  const [params, setParams] = useState<IParams>({
    page: 1,
    limit: 10,
  } as IParams);

  const [errors, setErrors] = useState<string[]>();
  const { data, meta, isLoading } = useList(params, token);

  const handleRequest = async (requestParams: any) => {
    setParams((prevState) => ({ ...prevState, ...requestParams }));
  };

  const handleEdit = async (id: string | number) => {
    setErrors([])
    setReadOnly(false);
    const editcustomChannel = data.filter((item) => item.ID_CANAL === id);
    if (editcustomChannel.length) {
      const { ID_CANAL, DESCRICAO, CODIGO_EDI, ATIVO } = editcustomChannel[0];
      setcustomChannel({ ID_CANAL, DESCRICAO, CODIGO_EDI, ATIVO });
      setModalOpen(true);
    }
    try {
      setControlEdit({
        loading: true,
        isInUse: true,
      });
      const { inUse } = await isInUse(id);
      setControlEdit({
        loading: false,
        isInUse: inUse,
      });
    } catch (error) {
      enqueueSnackbar("It's not posible to edit, try again later", {
        variant: "error",
      });
      setControlEdit({
        loading: false,
        isInUse: true,
      });
    }
  };

  const handleDeleteConfirmation = (id: string | number) => {
    setcustomChannelId(id);
    setOpenDeleteModal(true);
  };

  const handleDelete = async () => {
    if (customChannelId) {
      try {
        setIsSaving(true);
        await deleteCustomChannel(customChannelId);
        enqueueSnackbar("Custom Channel deleted successful", {
          variant: "success",
        });
        setParams({ ...params });
      } catch (err: any) {
        if (err.response.status) {
          enqueueSnackbar("Custom Channel is being used", { variant: "error" });
        } else {
          enqueueSnackbar(err.response.data.message[0], { variant: "error" });
        }
      }
      setIsSaving(false);
      setOpenDeleteModal(false);
    }
  };

  const handleFormSubmit = async (newcustomChannel: CustomChannelProps) => {
    try {
      setIsSaving(true);
      if (newcustomChannel.ID_CANAL) {
        await update(newcustomChannel.ID_CANAL, newcustomChannel);
        enqueueSnackbar("Custom Channel updated successful", {
          variant: "success",
        });
      } else {
        await create(newcustomChannel);
        enqueueSnackbar("Custom Channel created successful", {
          variant: "success",
        });
      }
      setcustomChannel({ ATIVO: true } as CustomChannelProps);
      setParams({ ...params });
      setModalOpen(false);
      setIsSaving(false);
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setErrors([err.response?.data?.message]);
      }
      setIsSaving(false);
    }
    setIsSaving(false);
  };

  const exportCustom = async () => {
    try {
      await exportAll(params);
    } catch (err) {}
  };

  const handleCancelEdit = () => {
    setcustomChannel({} as CustomChannelProps);
    setErrors([]);
    setModalOpen(false);
    setControlEdit({
      loading: false,
      isInUse: false,
    });
  };


  const onView = (id: string | number) => {
    setErrors([]);
    setReadOnly(true);
    setModalOpen(true);
    setcustomChannel(data.findLast((item) => item.ID_CANAL === id) || ({} as CustomChannelProps));
  };

  return (
    <Grid marginTop={4}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={9}>
          <Typography variant="h5">Custom Channel</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="center" marginTop={1}>
        <Grid item xs={2}>
          {!modalOpen ? (
            <Grid item xs={2}>
              <Button
                variant="contained"
                onClick={() => {
                  setControlEdit({
                    loading: false,
                    isInUse: false,
                  });
                  setErrors([])
                  setReadOnly(false);
                  setModalOpen(true);
                }}
                fullWidth
                sx={{
                  display: hasClaim("Custom Channel - Maintenance")
                    ? "block"
                    : "none",
                }}
                data-testid='new-button'
              >
                New
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
      {modalOpen ? (
        <Grid container alignItems="center">
          <CustomChannelForm
            onSubmit={handleFormSubmit}
            serviceLevel={customChannel}
            onCancel={handleCancelEdit}
            isLoading={isSaving}
            errors={errors}
            controlEdit={controlEdit}
            readOnly={readOnly}
          />
        </Grid>
      ) : null}
      <Search onChange={handleRequest} />
      <Grid container marginTop={2} justifyContent="flex-end">
        <Button variant="text" onClick={exportCustom} startIcon={<Download />}>
          Export XLSX
        </Button>
      </Grid>
      <Grid>
        <DataTable
          loading={isLoading}
          data={data}
          userValidation={userValidation}
          onDeleteServiceLevel={handleDeleteConfirmation}
          onEditServiceLevel={handleEdit}
          onViewServiceLevel={onView}
          rowCount={meta.total || 0}
          onPaginationModelChange={(e: any) => {
            setPaginationModel(e);
            handleRequest({ page: e.page + 1, limit: e.pageSize });
          }}
          paginationModel={paginationModel}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: params.limit } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
        />
      </Grid>
      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Custom Channel</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure that you want to delete this Custom Channel?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
          <Button onClick={handleDelete} data-testid="delete-modal-confirm">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
